import { fetchPipCreditsNotice, markPipCreditsSeen, resetPipCredits } from '@api/pipCredits';
import { ADMIN_JOBS_EVENT, isAdminJobsBlocking, isCompanyAdmin } from '@components/AdminJobs/events';
import { hasIncompleteOnboarding, isAutoOpenPath } from '@components/WhatsNew/events';
import { canSeeProductImprovement } from '@utils/helpers';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PipCreditsDialog from './PipCreditsDialog';
import { markPipCredits, pipCreditsDestination } from './events';

const CONSOLE_HELP = [
  'Product Improvement credits notice:',
  '  tripadmit.pipCredits()          show the notice now, if there is one',
  '  tripadmit.pipCredits.reset()    forget the notice and the PIP tour for you; reload to see both again',
  'Credits are set in the Multitool: Companies → company → Product Improvement credits.'
].join('\n');

/**
 * Card 457 (Andy, 2026-10-29): credits awarded in the Multitool used to go
 * unnoticed until the admin happened to open the PIP's Products page. Mounted
 * once in containers/App, after "Jobs to do" and before "What's new".
 *
 * - On the first dashboard page after sign-in it asks whether this admin has
 *   credits they haven't been told about, and if so opens once "Jobs to do"
 *   is out of the way. What's new waits for it (events.js).
 * - The dialog's button goes to the PIP: the guided tour for a first-timer,
 *   the add-a-tour form for everyone else.
 * - Every close counts as seen; the next notice is for the next increase.
 */
export function PipCreditsHost({ user }) {
  const history = useHistory();
  const location = useLocation();
  const userId = user?.userId;
  const enabled = !!userId && isCompanyAdmin(user) && canSeeProductImprovement(user);
  const onboarding = hasIncompleteOnboarding(user);

  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  // A notice that arrived while "Jobs to do" had the floor.
  const waiting = useRef(false);
  const autoChecked = useRef(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const show = useCallback(() => {
    waiting.current = false;
    setOpen(true);
    markPipCredits('open');
  }, []);

  // Open now if nothing ahead of it is showing; otherwise hold the floor
  // ('pending', so What's new keeps waiting) until the jobs dialog closes.
  const showWhenFree = useCallback(() => {
    if (isAdminJobsBlocking()) {
      waiting.current = true;
      markPipCredits('pending');
    } else {
      show();
    }
  }, [show]);

  // Automatic: once per signed-in admin per page load.
  useEffect(() => {
    if (!enabled || onboarding || autoChecked.current === userId || !isAutoOpenPath(location.pathname)) return;
    autoChecked.current = userId;
    markPipCredits('pending');
    fetchPipCreditsNotice()
      .then(result => {
        if (result?.notice && isAutoOpenPath(pathRef.current)) {
          setNotice(result.notice);
          showWhenFree();
        } else {
          markPipCredits(null);
        }
      })
      .catch(() => markPipCredits(null)); // never hold What's new hostage
  }, [enabled, onboarding, userId, location.pathname, showWhenFree]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onJobs = () => {
      if (waiting.current && !isAdminJobsBlocking()) show();
    };
    window.addEventListener(ADMIN_JOBS_EVENT, onJobs);
    return () => window.removeEventListener(ADMIN_JOBS_EVENT, onJobs);
  }, [enabled, show]);

  // The console command, for demos and QA.
  useEffect(() => {
    if (!enabled) return undefined;
    const pipCredits = () =>
      fetchPipCreditsNotice().then(result => {
        if (!result?.notice) return 'No notice: no credits this admin has not already been told about, or no free slot.';
        setNotice(result.notice);
        show();
        return 'Showing the Product Improvement credits notice';
      });
    pipCredits.reset = () =>
      resetPipCredits().then(() => 'Cleared. Reload the page and the notice opens by itself.');
    pipCredits.help = () => console.info(CONSOLE_HELP);
    window.tripadmit = { ...(window.tripadmit || {}), pipCredits };
    return () => {
      if (window.tripadmit && window.tripadmit.pipCredits === pipCredits) delete window.tripadmit.pipCredits;
    };
  }, [enabled, show]);

  // If the host unmounts (sign-out) mid-check, What's new must not wait forever.
  useEffect(() => () => markPipCredits(null), []);

  const close = useCallback(() => {
    setOpen(false);
    waiting.current = false;
    markPipCredits(null);
    markPipCreditsSeen().catch(() => {});
  }, []);

  const act = useCallback(() => {
    const to = pipCreditsDestination(notice);
    close();
    history.push(to);
  }, [notice, close, history]);

  if (!enabled) return null;

  return (
    <PipCreditsDialog
      open={open}
      notice={notice}
      companyName={user?.companyName}
      onClose={close}
      onAction={act}
    />
  );
}

PipCreditsHost.propTypes = {
  user: PropTypes.object
};

export default PipCreditsHost;
